import { ArrowLeft, ArrowRight } from 'lucide-react'
import { TopDiscountsTodayBaseProps, calculateDiscount, formatDiscount, formatPrice } from './TopDiscountsTodayBase'
import { TagBadgeMobile } from '@/components/ui/badge'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'

export const TopDiscountsTodayMobile = ({ items, loading = false, onItemClick, onBuyClick }: TopDiscountsTodayBaseProps) => {
  const { scrollContainerRef, isDragging, handlers, style } = useHorizontalScroll()

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -204,
        behavior: 'smooth'
      })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 204,
        behavior: 'smooth'
      })
    }
  }

  // Show loading state
  if (loading) {
    return (
      <div className="h-full">
        <div className="flex items-center justify-between">
          <h3 className="font-plus-jakarta font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] uppercase">
            Top Discounts Today
          </h3>
          <div className="flex items-center gap-2">
            <button
              className="w-[24px] h-[24px] flex items-center justify-center opacity-50"
              disabled
              aria-label="Previous"
            >
              <ArrowLeft className="w-[24px] h-[24px] text-gray-300" strokeWidth={1.5} />
            </button>
            <button
              className="w-[24px] h-[24px] flex items-center justify-center opacity-50"
              disabled
              aria-label="Next"
            >
              <ArrowRight className="w-[24px] h-[24px] text-gray-300" strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <div className="pt-[16px]">
          <p className="text-sm text-muted-foreground">Loading discounts...</p>
        </div>
      </div>
    )
  }

  // Show empty state
  if (items.length === 0) {
    return (
      <div className="h-full">
        <div className="flex items-center justify-between">
          <h3 className="font-plus-jakarta font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] uppercase">
            Top Discounts Today
          </h3>
          <div className="flex items-center gap-2">
            <button
              className="w-[24px] h-[24px] flex items-center justify-center opacity-50"
              disabled
              aria-label="Previous"
            >
              <ArrowLeft className="w-[24px] h-[24px] text-gray-300" strokeWidth={1.5} />
            </button>
            <button
              className="w-[24px] h-[24px] flex items-center justify-center opacity-50"
              disabled
              aria-label="Next"
            >
              <ArrowRight className="w-[24px] h-[24px] text-gray-300" strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <div className="pt-[16px]">
          <p className="text-sm text-muted-foreground">No discounted assets available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-plus-jakarta font-medium text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] uppercase">
          Top Discounts Today
        </h3>
        
        {/* Navigation arrows - size-[24px] */}
        <div className="flex items-center gap-2">
          <button
            onClick={scrollLeft}
            className="w-[24px] h-[24px] flex items-center justify-center transition-colors"
            aria-label="Previous"
          >
            <div className="rotate-180">
              <ArrowRight className="w-[24px] h-[24px] text-[#2d2923] dark:text-[#fdfdfd]" strokeWidth={1.5} />
            </div>
          </button>
          <button
            onClick={scrollRight}
            className="w-[24px] h-[24px] flex items-center justify-center transition-colors"
            aria-label="Next"
          >
            <ArrowRight className="w-[24px] h-[24px] text-[#2d2923] dark:text-[#fdfdfd]" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Cards container - horizontal scroll */}
      {/* Gap between cards: 8px */}
      <div 
        ref={scrollContainerRef}
        className="flex overflow-x-auto scrollbar-hide mt-[16px] -mx-[20px] px-[20px]"
        style={style}
        {...handlers}
      >
        {items.map((item, index) => {
          const discount = calculateDiscount(item)
          // Gap between cards: 8px
          const cardMarginRight = index < items.length - 1 ? 'mr-[8px]' : ''
          
          return (
            <div
              key={item.order_id}
              className={`flex-shrink-0 w-[280px] ${cardMarginRight} flex flex-col cursor-pointer`}
              onClick={() => {
                if (!isDragging) {
                  onItemClick?.(item)
                }
              }}
            >
              {/* Card content - gap-[18px] */}
              <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-[18px] p-[14px]">
                {/* Header with name and badge */}
                <div className="flex items-start justify-between gap-2">
                  {/* Name: text-[16px] leading-[24px] font-semibold */}
                  <h4 className="font-plus-jakarta font-semibold text-[16px] leading-[24px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {item.project_name}
                  </h4>
                  <TagBadgeMobile tag={item.tag} />
                </div>

                {/* Discount percentage and price - gap-[6px] */}
                <div className="flex flex-col gap-[6px]">
                  {/* Discount: text-[26px] leading-[36px] tracking-[-0.2px], percentage text-[16.77px] */}
                  <p className="font-plus-jakarta font-medium text-[#2d2923] dark:text-[#fdfdfd] leading-[36px] tracking-[-0.2px]">
                    <span className="text-[26px]">–{formatDiscount(discount)} </span>
                    <span className="text-[16.77px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
                  </p>
                  {/* Price: text-[13px] leading-[18px] */}
                  <p className="font-plus-jakarta font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                    {formatPrice(item)}
                  </p>
                </div>

                {/* Buttons - gap-[6px], vertical layout */}
                <div className="flex flex-col gap-[6px]">
                  {/* More details button */}
                  <button
                    className="w-full bg-white dark:bg-transparent border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] flex items-center justify-center px-[16px] py-[8px] font-plus-jakarta font-medium text-[12px] leading-[16px] text-[#2d2923] dark:text-[#fdfdfd] hover:bg-gray-50 dark:hover:bg-[rgba(253,253,253,0.1)] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      onItemClick?.(item)
                    }}
                  >
                    More details
                  </button>
                  {/* BUY button */}
                  <button
                    className="w-full bg-[#2d2923] dark:bg-[#fdfdfd] flex items-center justify-center px-[16px] py-[8px] font-plus-jakarta font-medium text-[12px] leading-[16px] text-white dark:text-[#2d2923] hover:bg-[#1a1612] dark:hover:bg-[rgba(253,253,253,0.9)] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      onBuyClick?.(item)
                    }}
                  >
                    BUY
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

